// UserPage.js
import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { fetchAuthSession } from "@aws-amplify/auth";
import { QRCodeCanvas } from "qrcode.react";
import { Modal } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import ElectrikCarousel from "./ElectrikCarousel";

const API_BASE_URL = "https://api.cupvisor.org";

// ── Added: list of language options (keep tiny & simple)
const LANGUAGE_OPTIONS = ["ES", "EN", "IT", "FR", "DE", "PT"];

function UserPage({ user, signOut }) {
  const [jwtToken, setJwtToken] = useState("");
  const [dashboardData, setDashboardData] = useState({ cards: [], prizes: [] });

  // ── Added: user language state + tiny UX flags
  const [userLanguage, setUserLanguage] = useState(null);
  const [isSavingLang, setIsSavingLang] = useState(false);
  const [langError, setLangError] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [rewardQuery, setRewardQuery] = useState("");
  const [qrModal, setQrModal] = useState({ show: false, url: "" });
  const { state } = useLocation();

  useEffect(() => {
    async function loadSession() {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        setJwtToken(idToken);
      } catch (err) {
        console.error("Error loading session:", err);
      }
    }
    loadSession();
  }, [user]);

  const fetchDashboard = useCallback(async () => {
    if (!jwtToken) return;
    try {
      const res = await fetch(`${API_BASE_URL}/customer/dashboard`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      });
      if (!res.ok) throw new Error(`API returned status ${res.status}`);
      const data = await res.json();
      setDashboardData(data);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    }
  }, [jwtToken]);

  // ── Added: fetch preferred language from /status_users
  const fetchUserLanguage = useCallback(async () => {
    if (!jwtToken) return;
    try {
      const res = await fetch(`${API_BASE_URL}/status_users`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      });
      if (!res.ok) throw new Error(`Lang GET failed: ${res.status}`);
      const data = await res.json();
      // API guarantees "language" (defaults to ES if none stored)
      if (data?.language) setUserLanguage(String(data.language).toUpperCase());
    } catch (err) {
      console.error("Error fetching user language:", err);
      setLangError("Unable to load preferred language.");
    }
  }, [jwtToken]);

  useEffect(() => {
    if (jwtToken) {
      fetchDashboard();
      fetchUserLanguage(); // ── Added
    }
  }, [jwtToken, fetchDashboard, fetchUserLanguage]);

  // ── Added: POST language update
  const updateLanguage = async (newLang) => {
    if (!jwtToken) return;
    setIsSavingLang(true);
    setLangError("");
    try {
      const res = await fetch(`${API_BASE_URL}/status_users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({ language: newLang }),
      });
      if (!res.ok) throw new Error(`Lang POST failed: ${res.status}`);
      const data = await res.json();
      // Optimistic update on success
      if (data?.language) setUserLanguage(String(data.language).toUpperCase());
    } catch (err) {
      console.error("Error updating language:", err);
      setLangError("Unable to save language. Please try again.");
    } finally {
      setIsSavingLang(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm("Are you sure you want to delete this card?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/customer/dashboard?cardId=${cardId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      });
      if (res.ok) {
        setDashboardData((prev) => ({
          ...prev,
          cards: prev.cards.filter((card) => card.card_id !== cardId),
        }));
      } else {
        console.error("Delete failed with status:", res.status);
      }
    } catch (err) {
      console.error("Error deleting card:", err);
    }
  };

  // Filter logic
  const filteredCards = dashboardData.cards.filter((card) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      card.vendor_name.toLowerCase().includes(searchLower) ||
      (card.description && card.description.toLowerCase().includes(searchLower))
    );
  });

  const filteredRewards = dashboardData.prizes.filter((reward) => {
    const searchLower = rewardQuery.toLowerCase();
    return (
      reward.vendor_name.toLowerCase().includes(searchLower) ||
      (reward.description && reward.description.toLowerCase().includes(searchLower))
    );
  });

  // Points rendering (dots or progress bar)
  const renderCardPoints = (points, maxPoints) => {
    if (maxPoints <= 10) {
      const stamps = [];
      for (let i = 0; i < maxPoints; i++) {
        stamps.push(
          i < points ? (
            <span key={i} style={{ fontSize: "1.2rem", color: "#ffffff", marginRight: "4px" }}>
              ●
            </span>
          ) : (
            <span key={i} style={{ fontSize: "1.2rem", color: "#cccccc", marginRight: "4px" }}>
              ○
            </span>
          )
        );
      }
      return (
        <div className="d-flex flex-column align-items-center justify-content-center mt-1">
          <div>{stamps}</div>
          <div style={{ fontSize: "0.9rem", color: "#ffffff" }}>
            {points}/{maxPoints}
          </div>
        </div>
      );
    } else {
      const percentage = (points / maxPoints) * 100;
      return (
        <div className="mt-1">
          <div className="progress" style={{ height: "20px", borderRadius: "10px" }}>
            <div
              className="progress-bar"
              role="progressbar"
              style={{
                width: `${percentage}%`,
                backgroundColor: "#ffcc00",
                color: "#333",
                fontWeight: "500",
              }}
              aria-valuenow={points}
              aria-valuemin={0}
              aria-valuemax={maxPoints}
            >
              {points}/{maxPoints}
            </div>
          </div>
        </div>
      );
    }
  };

  // QR Modal
  const openQrModal = (url) => setQrModal({ show: true, url });
  const closeQrModal = () => setQrModal({ show: false, url: "" });

  const noShadow = "none";
  const cardHeight = "220px";
  const carouselHeight = "240px";

  const renderCardSlide = (card) => (
    <div
      style={{
        border: "1px solid #ff66cc",
        borderRadius: "8px",
        overflow: "hidden",
        width: "280px",
        height: cardHeight,
        background: "linear-gradient(135deg, #ff66cc, #9933ff)",
        padding: "0.5rem",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxShadow: noShadow,
      }}
    >
      <div>
        <h6 style={{ fontWeight: "600", fontSize: "0.9rem", color: "#ffffff", marginBottom: "0.25rem" }}>
          {card.description || "Unnamed Card"}
        </h6>
        <p style={{ color: "#f0e6f7", margin: 0 }}>{card.vendor_name}</p>
        {renderCardPoints(card.points, card.max_points)}
      </div>
      <div className="d-flex justify-content-between align-items-center mt-1">
        <button
          onClick={() => handleDeleteCard(card.card_id)}
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "8px",
            border: "none",
            backgroundColor: "#ffcc00",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
          title="Delete Card"
        >
          <i className="bi bi-trash" style={{ fontSize: "1rem", color: "#ffffff" }}></i>
        </button>
        <div
          style={{
            position: "relative",
            width: "48px",
            height: "48px",
            cursor: "pointer",
          }}
          onClick={() => openQrModal(card.join_url)}
        >
          <QRCodeCanvas value={card.join_url} size={48} bgColor="#9933ff" />
          <i
            className="bi bi-arrow-up-right-square"
            style={{
              position: "absolute",
              bottom: "2px",
              right: "2px",
              fontSize: "0.9rem",
              color: "#ffffff",
            }}
          ></i>
        </div>
      </div>
    </div>
  );

  const renderRewardSlide = (reward) => (
    <div
      style={{
        border: "1px solid #ff66cc",
        borderRadius: "8px",
        overflow: "hidden",
        width: "280px",
        height: cardHeight,
        background: "linear-gradient(135deg, #ffcc00, #ff66cc)",
        padding: "0.5rem",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxShadow: noShadow,
      }}
    >
      <div>
        <h6 style={{ fontWeight: "600", fontSize: "0.9rem", color: "#ffffff", marginBottom: "0.25rem" }}>
          {reward.description || "Unnamed Reward"}
        </h6>
        <p style={{ color: "#fce8b2", margin: 0 }}>{reward.vendor_name}</p>
      </div>
      <div className="d-flex justify-content-between align-items-center mt-1">
        <a
          href={reward.redeem_url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm"
          style={{
            borderRadius: "8px",
            backgroundColor: "#9933ff",
            color: "#ffffff",
            fontWeight: "500",
            border: "none",
            padding: "4px 8px",
          }}
        >
          Redeem
        </a>
        <div
          style={{
            position: "relative",
            width: "48px",
            height: "48px",
            cursor: "pointer",
          }}
          onClick={() => openQrModal(reward.redeem_url)}
        >
          <QRCodeCanvas value={reward.redeem_url} size={48} bgColor="#ff66cc" />
          <i
            className="bi bi-arrow-up-right-square"
            style={{
              position: "absolute",
              bottom: "2px",
              right: "2px",
              fontSize: "0.9rem",
              color: "#ffffff",
            }}
          ></i>
        </div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        minHeight: "100vh",
        margin: 0,
        padding: 0,
      }}
    >
      {state && state.message && (
        <div
          className="alert alert-success"
          style={{ margin: "0.5rem auto", width: "90%" }}
        >
          {state.message}
        </div>
      )}

      {/* ── Added: Language row (small, centered, minimal) */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            padding: "6px 10px",
            borderRadius: "12px",
            border: "1px solid #eee",
            background: "#fafafa",
          }}
        >
          <span style={{ fontSize: "0.9rem", color: "#333" }}>
            Preferred language:
          </span>
          <strong style={{ fontSize: "0.9rem" }}>
            {userLanguage || "—"}
          </strong>
          <select
            disabled={!userLanguage || isSavingLang}
            value={userLanguage || ""}
            onChange={(e) => updateLanguage(e.target.value)}
            style={{
              borderRadius: "10px",
              padding: "4px 8px",
              fontSize: "0.85rem",
              border: "1px solid #ddd",
              background: "#fff",
            }}
          >
            {LANGUAGE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {isSavingLang && (
            <span style={{ fontSize: "0.8rem", color: "#666" }}>Saving…</span>
          )}
        </div>
      </div>
      {langError && (
        <div
          className="alert alert-warning"
          style={{ margin: "0.5rem auto", width: "90%" }}
        >
          {langError}
        </div>
      )}

      {/* A bit of space from the top for "Find Your Card" */}
      <div style={{ textAlign: "center", marginTop: "2.5rem", marginBottom: "1.5rem" }}>
        <input
          type="text"
          className="form-control d-inline-block text-center"
          placeholder="Find Your Card..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: "160px",
            borderRadius: "20px",
            fontSize: "0.85rem",
            fontWeight: "600",
            padding: "4px 8px",
            textAlign: "center",
            margin: 0,
            color: "#000",
            opacity: 1,
          }}
        />
      </div>

      {/* Cards Carousel - minimal spacing below */}
      <ElectrikCarousel
        items={filteredCards}
        renderSlide={renderCardSlide}
        containerStyle={{
          width: "100%",
          height: carouselHeight,
          margin: "0 auto",
          marginBottom: "0.5rem",
        }}
      />

      {/* Reward Search Bar - 70% reduced space from above, minimal margin */}
      <div style={{ textAlign: "center", marginTop: "0rem", marginBottom: "1.5rem" }}>
        <input
          type="text"
          className="form-control d-inline-block text-center"
          placeholder="Find Your Reward..."
          value={rewardQuery}
          onChange={(e) => setRewardQuery(e.target.value)}
          style={{
            width: "160px",
            borderRadius: "20px",
            fontSize: "0.85rem",
            fontWeight: "600",
            padding: "4px 8px",
            textAlign: "center",
            margin: 0,
            color: "#000",
            opacity: 1,
          }}
        />
      </div>

      {/* Rewards Carousel */}
      <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
        {dashboardData.prizes && dashboardData.prizes.length > 0 ? (
          <ElectrikCarousel
            items={filteredRewards}
            renderSlide={renderRewardSlide}
            containerStyle={{
              width: "100%",
              height: carouselHeight,
              margin: "0 auto",
            }}
          />
        ) : (
          <p style={{ color: "#333", margin: 0 }}>No prizes available.</p>
        )}
      </div>

      {/* QR Modal */}
      <Modal show={qrModal.show} onHide={closeQrModal} centered>
        <Modal.Header closeButton style={{ borderBottom: "none" }} />
        <Modal.Body
          className="text-center"
          style={{ backgroundColor: "#9933ff", padding: "2rem" }}
        >
          <QRCodeCanvas
            value={qrModal.url}
            size={300}
            bgColor="#9933ff"
            fgColor="#ffffff"
            level="H"
            style={{ margin: "auto" }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default UserPage;